export const IMAGE_RATIO = 2.12
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const MAX_IMAGE_ZOOM = 4
export type ImageCrop = {version: 1; originalWidth: number; originalHeight: number; x: number; y: number; width: number; height: number}
export type TariffImage = {
  imageId?: number
  mimeType: 'image/png' | 'image/jpeg'
  crop?: ImageCrop
  originalSrc?: string
  editedSrc?: string
  pending?: boolean
}
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
export const centralCrop = (width: number, height: number): ImageCrop => {
  const cropWidth = Math.min(width, height * IMAGE_RATIO)
  const cropHeight = cropWidth / IMAGE_RATIO
  return {version: 1, originalWidth: width, originalHeight: height,
    x: (width - cropWidth) / 2, y: (height - cropHeight) / 2, width: cropWidth, height: cropHeight}
}
export const constrainCrop = (crop: ImageCrop): ImageCrop => ({...crop,
  x: clamp(crop.x, 0, Math.max(0, crop.originalWidth - crop.width)),
  y: clamp(crop.y, 0, Math.max(0, crop.originalHeight - crop.height))})
export const fitCropToImageRatio = (crop: ImageCrop): ImageCrop => {
  const width = Math.min(crop.width, crop.height * IMAGE_RATIO)
  const height = width / IMAGE_RATIO
  return constrainCrop({...crop, width, height,
    x: crop.x + (crop.width - width) / 2, y: crop.y + (crop.height - height) / 2})
}
export const cropZoom = (crop: ImageCrop) => centralCrop(crop.originalWidth, crop.originalHeight).width / crop.width
export const cropPreviewLayout = (crop: ImageCrop, stageRatio = 1.3) => {
  const baseWidth = centralCrop(crop.originalWidth, crop.originalHeight).width
  const frameWidth = crop.width / baseWidth
  const frameHeight = crop.height / baseWidth * stageRatio
  return {
    imageWidth: crop.originalWidth / baseWidth,
    imageHeight: crop.originalHeight / baseWidth * stageRatio,
    imageLeft: 0.5 - (crop.x + crop.width / 2) / baseWidth,
    imageTop: 0.5 - (crop.y + crop.height / 2) / baseWidth * stageRatio,
    frameWidth, frameHeight, frameLeft: (1 - frameWidth) / 2, frameTop: (1 - frameHeight) / 2
  }
}
export const zoomCrop = (crop: ImageCrop, zoom: number): ImageCrop => {
  const width = centralCrop(crop.originalWidth, crop.originalHeight).width / clamp(zoom, 1, MAX_IMAGE_ZOOM)
  const height = width / IMAGE_RATIO
  return constrainCrop({...crop, width, height,
    x: crop.x + (crop.width - width) / 2, y: crop.y + (crop.height - height) / 2})
}
export const moveCrop = (crop: ImageCrop, dx: number, dy: number): ImageCrop => constrainCrop({...crop, x: crop.x + dx, y: crop.y + dy})
export const sameCrop = (a?: ImageCrop, b?: ImageCrop) => !!a && !!b &&
  ['x', 'y', 'width', 'height', 'originalWidth', 'originalHeight'].every(key =>
    Math.abs(a[key as keyof ImageCrop] - b[key as keyof ImageCrop]) < 0.0001)
export const parseCrop = (value: unknown): ImageCrop | undefined => {
  try {
    const crop = typeof value === 'string' ? JSON.parse(value) : value
    if (!crop || crop.version !== 1 || !['originalWidth', 'originalHeight', 'x', 'y', 'width', 'height']
      .every(key => typeof crop[key] === 'number' && Number.isFinite(crop[key]))) return
    if (crop.originalWidth <= 0 || crop.originalHeight <= 0 || crop.width <= 0 || crop.height <= 0
      || crop.width > crop.originalWidth || crop.height > crop.originalHeight
      // Keep saved 16:9 coordinates so reopening the crop preserves its center.
      || ![IMAGE_RATIO, 16 / 9].some(ratio => Math.abs(crop.width / crop.height - ratio) <= 0.001)) return
    return constrainCrop({...crop})
  } catch { return }
}
export const imageOutputSize = (crop: ImageCrop) => {
  const width = Math.max(1, Math.floor(Math.min(1600, crop.width)))
  return {width, height: Math.max(1, Math.round(width / IMAGE_RATIO))}
}
export class TariffImageError extends Error {
  constructor(public key: 'format' | 'size' | 'read') { super(key) }
}
const readDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result))
  reader.onerror = () => reject(new TariffImageError('read'))
  reader.onabort = () => reject(new TariffImageError('read'))
  reader.readAsDataURL(blob)
})
export const decodeImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => image.naturalWidth && image.naturalHeight ? resolve(image) : reject(new TariffImageError('read'))
  image.onerror = () => reject(new TariffImageError('read'))
  image.src = src
})
export const renderCrop = async (image: TariffImage, crop: ImageCrop): Promise<string> => {
  const original = await decodeImage(image.originalSrc!)
  const size = imageOutputSize(crop)
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height
  const context = canvas.getContext('2d')
  if (!context) throw new TariffImageError('read')
  context.drawImage(original, crop.x, crop.y, crop.width, crop.height, 0, 0, size.width, size.height)
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value =>
    value ? resolve(value) : reject(new TariffImageError('read')), image.mimeType, 0.92))
  return readDataUrl(blob)
}
export const loadImageFile = async (file: Blob): Promise<TariffImage> => {
  if (file.size > MAX_IMAGE_BYTES) throw new TariffImageError('size')
  const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer())
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => bytes[index] === byte)
  const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255
  if (!png && !jpeg) throw new TariffImageError('format')
  const mimeType = png ? 'image/png' : 'image/jpeg'
  const originalSrc = await readDataUrl(file.type === mimeType ? file : new Blob([file], {type: mimeType}))
  const original = await decodeImage(originalSrc)
  const crop = centralCrop(original.naturalWidth, original.naturalHeight)
  const image: TariffImage = {mimeType, originalSrc, crop, pending: true}
  image.editedSrc = await renderCrop(image, crop)
  return image
}
export const editableImage = async (image: TariffImage): Promise<TariffImage> => {
  if (!image.originalSrc) throw new TariffImageError('read')
  let loaded: TariffImage
  if (image.originalSrc.startsWith('data:')) {
    const original = await decodeImage(image.originalSrc)
    loaded = {...image, crop: centralCrop(original.naturalWidth, original.naturalHeight)}
  } else {
    const response = await fetch(image.originalSrc, {credentials: 'same-origin'})
    if (!response.ok) throw new TariffImageError('read')
    loaded = await loadImageFile(await response.blob())
  }
  const previous = parseCrop(image.crop)
  const crop = previous && previous.originalWidth === loaded.crop!.originalWidth
    && previous.originalHeight === loaded.crop!.originalHeight ? previous : loaded.crop!
  return {...loaded, imageId: image.imageId, pending: image.pending, crop, editedSrc: image.editedSrc || loaded.editedSrc}
}
export const imagePathUrl = (path: string) => /^(https?:|data:|blob:|\/proxy\/)/.test(path) ? path
  : `/proxy/tap-api.tap/api/v1/image/by-path?path=${encodeURIComponent(path)}`
